import React from 'react';
import { makeStyles } from 'design-system';
import Markdown from '@scale/frontend-lib/markdown';
import Section from './section';
import Example from './example';
import {
  IInstructionExampleJson,
  FieldInstructionJson,
  FieldChoiceInstructionJson,
} from '@scale/shared/projectInstruction/types';
import { IOntologyChoice } from '@scale/shared/types/ontology';
import { useProjectInstructionContext } from '../projectInstructionContext';
import apiFetch from 'react-scale/lib/api-fetch';
import { InstructionExampleSampleType } from '@scale/shared/defaults';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Field = ({ contentData }: { contentData: FieldInstructionJson }) => {
  const classes = useStyles();
  const {
    title,
    examples,
    description,
    choices: choicesFromProps,
    ontologyProjectId,
  } = contentData;
  const { isMobileView } = useProjectInstructionContext();
  const [choices, setChoices] = React.useState<FieldChoiceInstructionJson[]>(
    choicesFromProps || [],
  );

  React.useEffect(() => {
    if (ontologyProjectId) {
      const getOntology = async () => {
        try {
          const { ontology } = await apiFetch<{ ontology: IOntologyChoice[] }>(
            null,
            `/internal/ontology_manager/latest_ontology/${ontologyProjectId}`,
            {
              method: 'GET',
            },
          );
          const convertOntologyToChoices = (
            ontology: IOntologyChoice[],
          ): FieldChoiceInstructionJson[] =>
            _.sortBy(
              ontology.map(choice => {
                return {
                  _id: choice.value!,
                  choice: choice.display!,
                  subchoices: convertOntologyToChoices(choice.subchoices || []),
                  title: '',
                  description: '',
                  examples: [
                    {
                      _id: '',
                      title: '',
                      description: choice.description || '',
                      samples: (choice.videoUrlExamples || []).map((videoUrl: string) => ({
                        _id: videoUrl,
                        type: InstructionExampleSampleType.Good,
                        videoUrl,
                      })),
                    },
                  ],
                };
              }),
              a => a.choice,
            );
          const choicesFromOntology = convertOntologyToChoices(ontology);
          setChoices(choicesFromOntology);
        } catch (error) {
          // console.log('error', error);
        }
      };
      getOntology();
    }
  }, [ontologyProjectId]);

  return (
    <div>
      {!isMobileView && (
        <div className={classes.titleContainer}>
          <h3 className={classes.title}>{title}</h3>
          {contentData.hiddenFromCustomer && (
            <FontAwesomeIcon className={classes.hideIcon} icon="eye-slash" />
          )}
        </div>
      )}
      {description && (
        <Section sectionName="description">
          <div
            dangerouslySetInnerHTML={{
              __html: Markdown.render(description || ''),
            }}
          />
        </Section>
      )}
      {!!examples?.length && (
        <Section sectionName="examples" isNew={false}>
          {examples.map((example: IInstructionExampleJson, index: number) => {
            return (
              <div
                key={`${index}-${index}`}
                style={{
                  borderBottom: index !== examples.length - 1 ? `3px solid #E6E6E6` : 'transparent',
                }}
              >
                <Example key={index} example={example} />
              </div>
            );
          })}
        </Section>
      )}
      {choices && choices.length > 0 && (
        <Section sectionName="Taxonomy" defaultOpen={false}>
          {choices.map(choice => (
            <ChoiceView key={choice._id} choice={choice} />
          ))}
        </Section>
      )}
    </div>
  );
};

const ChoiceView = ({ choice }: { choice: FieldChoiceInstructionJson }) => {
  const { choice: choiceName, subchoices, examples } = choice;
  return (
    <Section sectionName={choiceName} defaultOpen={false}>
      <>
        {!!examples?.length && (
          <Section sectionName="examples" isNew={false}>
            {examples.map((example: IInstructionExampleJson, index: number) => {
              return (
                <div
                  key={`${index}-${index}`}
                  style={{
                    borderBottom:
                      index !== examples.length - 1 ? `3px solid #E6E6E6` : 'transparent',
                  }}
                >
                  <Example key={index} example={example} />
                </div>
              );
            })}
          </Section>
        )}
      </>
      <>
        {subchoices.length > 0 && (
          <div>
            {subchoices.map(subchoice => (
              <ChoiceView key={subchoice._id} choice={subchoice} />
            ))}
          </div>
        )}
      </>
    </Section>
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: 600,
  },
  hideIcon: {
    color: theme.colors.gray50,
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

export default Field;
